import React from 'react';
import {
  IconButton,
  MenuItem,
  Select,
  Typography,
  Box,
  Button
} from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i);
  } else {
    if (currentPage <= 3) {
      pages.push(0, 1, 2, 3, 4, 'ellipsis', totalPages - 1);
    } else if (currentPage >= totalPages - 4) {
      pages.push(
        0,
        'ellipsis',
        totalPages - 5,
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1
      );
    } else {
      pages.push(
        0,
        'ellipsis',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        'ellipsis',
        totalPages - 1
      );
    }
  }
  return pages;
};

const TablePaginationForSyncedDataComponent = ({
  currentPage,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  totalRows,
  totalPages,
  rowsPerPageOptions = [10, 25, 50, 100]
}) => {
  const startRow = totalRows === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endRow = Math.min(startRow + rowsPerPage - 1, totalRows);

  const handlePreviousPage = () => {
    if (currentPage > 0) onPageChange(null, currentPage - 1);  // ✅ Pass null as event
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) onPageChange(null, currentPage + 1);  // ✅ Pass null as event
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    onRowsPerPageChange(newSize);
    onPageChange(null, 0);
  };

  const handlePageClick = (page) => {
    if (page !== 'ellipsis' && page !== currentPage) {
      onPageChange(null, page);
    }
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center !important', sm: 'space-between !important' },
        gap: { xs: 2, sm: 3 },
        py: 2,
        px: { xs: 2, sm: 3 },
        backgroundColor: (theme) => theme.palette.background.default,
        borderTop: '1px solid',
        borderColor: 'divider',
        flexWrap: { xs: 'wrap', sm: 'nowrap' }
      }}
    >
      {/* Rows per page selector */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'space-between !important'},
          width: { xs: '100% !important', sm: 'auto !important' },
          gap: 1,
          order: { xs: 1 }
        }}
      >
        <Typography
          variant='body2'
          component='span'
          sx={{
            whiteSpace: 'nowrap',
            color: 'text.secondary'
          }}
        >
          Rows per page:
        </Typography>
        <Select
          size='small'
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          sx={{
            fontSize: 13,
            width: 70,
            height: 32,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {rowsPerPageOptions.map((rowsOption) => (
            <MenuItem key={rowsOption} value={rowsOption} sx={{ fontSize: 13 }}>
              {rowsOption}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Number of rows displayed */}
      <Typography
        variant='body2'
        sx={{
          color: 'text.secondary',
          order: { xs: 3 },
          width: { xs: '100%', sm: 'auto' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        Showing {startRow} to {endRow} of {totalRows} entries
      </Typography>

      {/* Pagination Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
          order: { xs: 3, sm: 3 },
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'center', sm: 'flex-end' }
        }}
      >
        <IconButton
          size='small'
          disabled={currentPage === 0}
          onClick={handlePreviousPage}
          sx={{
            color: 'text.secondary',
            '&:hover:not(:disabled)': {
              backgroundColor: 'action.hover',
              color: 'primary.main'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <ChevronLeft size={18} />
        </IconButton>
        {pageNumbers.map((page, idx) =>
          page === 'ellipsis' ? (
            <Typography
              key={`ellipsis-${idx}`}
              sx={{
                px: 0.5,
                color: 'text.disabled',
                userSelect: 'none'
              }}
            >
              …
            </Typography>
          ) : (
            <Button
              key={page}
              size='small'
              variant={page === currentPage ? 'contained' : 'text'}
              color={page === currentPage ? 'primary' : 'inherit'}
              onClick={() => handlePageClick(page)}
              sx={{
                minWidth: 32,
                height: 32,
                px: 0,
                fontWeight: page === currentPage ? 600 : 400,
                color: page === currentPage ? 'white' : 'text.secondary',
                '&:hover': {
                  backgroundColor: page === currentPage ? 'primary.dark' : 'action.hover',
                  color: page === currentPage ? 'white' : 'primary.main'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {page + 1}
            </Button>
          )
        )}
        <IconButton
          size='small'
          disabled={currentPage === totalPages - 1 || totalRows === 0}
          onClick={handleNextPage}
          sx={{
            color: 'text.secondary',
            '&:hover:not(:disabled)': {
              backgroundColor: 'action.hover',
              color: 'primary.main'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <ChevronRight size={18} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TablePaginationForSyncedDataComponent;
